// src/components/UIComponents.jsx - Fixed version
import { useState } from 'react';
import { Check, Clock, Download } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../services/api';

export const StatCard = ({ title, value, icon }) => {
  return (
    <div className="bg-gray-800 rounded-lg shadow p-6">
      <div className="flex justify-between">
        <div>
          <p className="text-gray-400 text-sm">{title}</p>
          <p className="text-2xl font-semibold mt-1 text-white">{value}</p>
        </div>
        {icon}
      </div>
    </div>
  );
};

export const RenderItem = ({ render }) => {
  const { t } = useLanguage();
  const [isDownloading, setIsDownloading] = useState(false);
  
  const handleDownload = async () => {
    if (render.status !== 'completed') return;
    
    try {
      setIsDownloading(true);
      console.log(`Starting download for render: ${render.id}`);
      
      const result = await api.getRenderOutput(render.id);
      
      if (result.success) {
        if (result.type === 'direct_download') {
          console.log('Download started successfully');
        } else if (result.data && result.data.outputUrl) {
          const link = document.createElement('a');
          link.href = result.data.outputUrl;
          link.download = result.data.fileName || `${render.name}.mp4`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      alert(`Download failed: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };
  
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-700 last:border-0">
      <div>
        <h3 className="font-medium text-white">{render.name}</h3>
        <p className="text-gray-400 text-sm mt-1">{render.duration} · {render.resolution}</p>
      </div>
      <div className="flex items-center space-x-3">
        <RenderStatus status={render.status} />
        {render.status === 'completed' && (
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center text-sm"
            title="Download render"
          >
            <Download size={16} className="mr-1" />
            {isDownloading ? 'Downloading...' : 'Download'}
          </button>
        )}
      </div>
    </div>
  );
};

export const RenderStatus = ({ status }) => {
  const { t } = useLanguage();
  
  if (status === 'completed') {
    return (
      <span className="bg-gray-700 text-green-400 text-xs px-2 py-1 rounded-full flex items-center">
        <Check size={12} className="mr-1" /> {t('complete')}
      </span>
    );
  } 
  else if (status === 'rendering') {
    return (
      <span className="bg-gray-700 text-yellow-400 text-xs px-2 py-1 rounded-full flex items-center">
        <Clock size={12} className="mr-1" /> {t('rendering')}
      </span>
    );
  }
  else if (status === 'queued') {
    return (
      <span className="bg-gray-700 text-blue-400 text-xs px-2 py-1 rounded-full flex items-center">
        <Clock size={12} className="mr-1" /> {t('queued')}
      </span>
    );
  }
  return null;
};

export const StepIndicator = ({ number, text, active, completed }) => {
  const { t } = useLanguage();
  
  let circleClasses = "flex items-center justify-center w-8 h-8 rounded-full";
  let lineClasses = "flex-1 h-1";
  
  if (completed) {
    circleClasses += " bg-red-600 text-white";
    lineClasses += " bg-red-600";
  } else if (active) {
    circleClasses += " bg-red-500 text-white";
    lineClasses += " bg-gray-600";
  } else {
    circleClasses += " bg-gray-600 text-gray-400";
    lineClasses += " bg-gray-600";
  }
  
  return (
    <div className="flex items-center flex-1">
      <div className={circleClasses}>
        {completed ? <Check size={16} /> : number}
      </div>
      {number !== "3" && <div className={lineClasses + " mx-2"} />}
      <div className={`ml-2 ${active || completed ? "text-white" : "text-gray-400"}`}>
        {text}
      </div>
    </div>
  );
};